import { useEffect } from "react";
import { Modal } from "@murm/ant";
import { formatClockTime } from "../lib/formatClockTime";

export const SESSION_WARNING_LEAD_MS = 5 * 60 * 1000;
export const SESSION_EXPIRY_CHECK_INTERVAL_MS = 30 * 1000;

const msUntil = (timestampMs) => Math.max(0, timestampMs - Date.now());

// No-op without `expiresAt`: auth0-spa mode has none because Auth0 refreshes tokens silently.
export function useSessionExpiryWarning(expiresAt, onExpire) {
  useEffect(() => {
    if (!expiresAt) return undefined;

    let hasExpired = false;
    const expire = () => {
      if (hasExpired) return;
      hasExpired = true;
      Modal.destroyAll();
      onExpire();
    };
    // Timers stop counting while the machine sleeps or the tab is throttled, so an overnight
    // timeout can still be pending long after the session ended; the wall clock is the truth.
    const expireIfPastDeadline = () => {
      if (Date.now() >= expiresAt) expire();
    };
    const expireIfVisibleAndPastDeadline = () => {
      if (document.visibilityState === "visible") expireIfPastDeadline();
    };

    const warnTimer = setTimeout(
      () =>
        Modal.warning({
          title: "You will be logged out soon",
          content: `Your session ends at ${formatClockTime(expiresAt)}. Save your work before then.`,
          okText: "OK",
          // Static modals render outside DesignSystemProvider, so they miss its centered default.
          centered: true,
        }),
      msUntil(expiresAt - SESSION_WARNING_LEAD_MS)
    );
    const expireTimer = setTimeout(expire, msUntil(expiresAt));
    const deadlineCheck = setInterval(expireIfPastDeadline, SESSION_EXPIRY_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", expireIfVisibleAndPastDeadline);
    window.addEventListener("focus", expireIfPastDeadline);

    return () => {
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
      clearInterval(deadlineCheck);
      document.removeEventListener("visibilitychange", expireIfVisibleAndPastDeadline);
      window.removeEventListener("focus", expireIfPastDeadline);
    };
  }, [expiresAt, onExpire]);
}
